import React, { useEffect, useRef } from "react";
import { useRevealGroup } from "../../hooks/useReveal";

/**
 * Section ② — Summary. Cinematic background video served from the same
 * origin. We offer two same-codec H.264 sources so the browser picks
 * the lighter one first (it falls back automatically if it can't load):
 *   • 1080p · 8 Mbps · faststart  → 35 MB (primary, plays everywhere)
 *   • 4K UHD · 25 Mbps · faststart → 108 MB (fallback for high-end desktops)
 *
 * After blur(18px) + scale(1.1) the human eye cannot distinguish the
 * two — but the 1080p source lets the browser actually start autoplay
 * within seconds. Four-layer stack:
 *   z-0  <video>           — source, blurred 18px + scale 1.1
 *   z-1  .summary-blur     — backdrop-blur 8px
 *   z-2  .summary-dark     — rgba(8,8,8,0.80) for legibility
 *   z-3  .summary-content  — editorial copy (unchanged)
 *
 * Robust autoplay:
 *   • muted attribute set imperatively BEFORE play() (React's `muted`
 *     prop alone isn't always picked up early enough for autoplay).
 *   • play() invoked on mount, on viewport enter, AND on first user
 *     interaction (scroll/click/touch) to defeat strict autoplay
 *     policies that throttle a second <video> on the page.
 */
const SUMMARY_VIDEO_1080 = "/media/summary-cinematic-1080.mp4";
const SUMMARY_VIDEO_4K = "/media/summary-cinematic.mp4";

const DISCIPLINES = [
  "THE CAPITAL OFFICE",
  "THE DEALROOM",
  "THE ESTATES",
  "THE ATELIER",
];

const SummaryHero = () => {
  const ref = useRevealGroup(".reveal");
  const videoRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    // Hard-set the muted attribute before play() — Chrome/Safari sometimes
    // ignore React's prop on first paint, which causes autoplay to fail.
    v.muted = true;
    v.defaultMuted = true;
    v.setAttribute("muted", "");
    v.setAttribute("playsinline", "");
    v.setAttribute("webkit-playsinline", "");

    const tryPlay = () => {
      const p = v.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => {
          // Autoplay was blocked — will retry on next user interaction.
        });
      }
    };

    // Attempt 1: immediately on mount
    tryPlay();

    // Attempt 2: whenever the section enters the viewport
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => { if (e.isIntersecting) tryPlay(); });
      },
      { threshold: 0.05 }
    );
    io.observe(v);

    // Attempt 3: on first user interaction of any kind
    const onInteract = () => {
      tryPlay();
      window.removeEventListener("scroll", onInteract);
      window.removeEventListener("click", onInteract);
      window.removeEventListener("touchstart", onInteract);
      window.removeEventListener("keydown", onInteract);
    };
    window.addEventListener("scroll", onInteract, { passive: true });
    window.addEventListener("click", onInteract);
    window.addEventListener("touchstart", onInteract, { passive: true });
    window.addEventListener("keydown", onInteract);

    return () => {
      io.disconnect();
      window.removeEventListener("scroll", onInteract);
      window.removeEventListener("click", onInteract);
      window.removeEventListener("touchstart", onInteract);
      window.removeEventListener("keydown", onInteract);
    };
  }, []);

  return (
    <section
      ref={ref}
      className="summary-section relative overflow-hidden"
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "transparent",
        marginTop: -1,
      }}
      data-testid="summary-section"
    >
      {/* z-0 — background video, original UHD quality */}
      <video
        ref={videoRef}
        className="summary-bg"
        autoPlay
        muted
        defaultMuted
        loop
        playsInline
        preload="auto"
        disablePictureInPicture
        controlsList="nodownload"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          objectPosition: "center",
          filter: "blur(18px)",
          WebkitFilter: "blur(18px)",
          transform: "scale(1.1)",
          zIndex: 0,
          pointerEvents: "none",
        }}
        aria-hidden="true"
        data-testid="summary-bg-video"
      >
        <source src={SUMMARY_VIDEO_1080} type="video/mp4" />
        <source src={SUMMARY_VIDEO_4K} type="video/mp4" />
      </video>

      {/* z-1 — backdrop blur layer */}
      <div
        className="summary-blur"
        style={{
          position: "absolute",
          inset: 0,
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
          zIndex: 1,
          pointerEvents: "none",
        }}
        aria-hidden="true"
      />

      {/* z-2 — dark legibility overlay */}
      <div
        className="summary-dark"
        style={{
          position: "absolute",
          inset: 0,
          backgroundColor: "rgba(8,8,8,0.80)",
          zIndex: 2,
          pointerEvents: "none",
        }}
        aria-hidden="true"
      />

      {/* z-3 — centered editorial content */}
      <div
        className="summary-content relative mx-auto text-center"
        style={{
          zIndex: 3,
          maxWidth: 900,
          padding: "120px 40px",
        }}
      >
        <div className="reveal">
          <span
            className="inline-block uppercase"
            style={{
              fontFamily: "'Jost', sans-serif",
              fontWeight: 400,
              fontSize: 9,
              letterSpacing: "0.52em",
              color: "#C8A95A",
            }}
          >
            HOUSE OF NUMBARDAR
          </span>
        </div>

        <h2
          className="reveal delay-1 mt-8"
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 500,
            color: "#FFFFFF",
            fontSize: "clamp(38px, 4.5vw, 58px)",
            lineHeight: 1.15,
          }}
        >
          Not a brokerage.
          <br />
          A private institution.
        </h2>

        <p
          className="reveal delay-2 mx-auto mt-10"
          style={{
            fontFamily: "'Libre Baskerville', Georgia, serif",
            fontSize: 16,
            color: "rgba(255,255,255,0.65)",
            lineHeight: 1.9,
            maxWidth: 640,
          }}
        >
          Two centuries of land custodianship, reimagined for modern India.
          We serve families, investors and institutions who demand more than
          a broker — bringing portfolio thinking, deep market intelligence,
          and the discretion of a trusted advisor to every transaction.
          <br />
          <br />
          Four disciplines. One institution. One legacy.
        </p>

        <div
          className="reveal delay-3 mt-14 flex flex-wrap items-center justify-center gap-x-6 gap-y-3"
          data-testid="summary-disciplines"
        >
          {DISCIPLINES.map((label, i) => (
            <React.Fragment key={label}>
              <span
                className="uppercase"
                style={{
                  fontFamily: "'Jost', sans-serif",
                  fontWeight: 400,
                  fontSize: 9,
                  letterSpacing: "0.44em",
                  color: "rgba(255,255,255,0.5)",
                }}
              >
                {label}
              </span>
              {i < DISCIPLINES.length - 1 && (
                <span
                  aria-hidden="true"
                  className="hidden sm:block"
                  style={{
                    width: 1,
                    height: 12,
                    backgroundColor: "rgba(255,255,255,0.15)",
                  }}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SummaryHero;
